import type { components } from "@cntrl/api-client";
import { formatMoneyCents } from "../../app/hooks/inboxFormat.js";

type OpportunityDto = components["schemas"]["OpportunityDto"];

/**
 * Opportunity DISPLAY rules, shared by the desktop card and the mobile home so the two can't drift.
 *
 * **Expiry is display-only** (`dashboard.txt:77`). A past opportunity is hidden from the feed but NEVER
 * deleted — it still counts for history and the calendar. The count tag derives from the FILTERED list, so
 * the badge and the rows always agree.
 *
 * **Money is display-only here too, and never fetched here.** The figure arrives from the separate
 * money-gated sub-resource (`useOpportunityMoney`); this module only says how it READS once a gated caller
 * has it, so the desktop card and the mobile row cannot drift on the format or the separator.
 *
 * The two money halves stay SEPARATE helpers rather than one joined string, because the surfaces place them
 * differently: the desktop card gives money its own line under the title (the specimen's `.opp__money` —
 * amount in accent mono, terms beside it in body voice), while the phone has one truncating sub-line per row
 * and carries date, amount and terms in it together.
 */

/**
 * The minimum an item needs for the feed's expiry rule. Deliberately STRUCTURAL, not `OpportunityDto`: the
 * card's second source is a show/hold invite from Booking, which is dated the same way and must expire by
 * the same rule. One rule, both sources — the reason this module exists.
 */
export interface FeedTiming {
  date?: string | null;
  startTime?: string | null;
}

/** What `answeredByViewer` reads — either family's way of carrying the caller's own answer. */
export type FeedAnswer = {
  myRsvp?: string | null;
  invites?: readonly { inviteePersonId?: string | null; response?: string | null }[] | null;
};

/** Whether a dated feed item still belongs in the feed, judged in DEVICE-LOCAL wall clock (never UTC). */
export function isLive(opp: FeedTiming, now: Date = new Date()): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(opp.date ?? "");
  if (!m) return true; // undated opportunities never expire

  const [y, mo, d] = [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
  const t = /^(\d{1,2}):(\d{2})/.exec(opp.startTime ?? "");

  if (t) {
    // Timed: hide once it is more than an hour past its moment.
    const at = new Date(y, mo, d, Number(t[1]), Number(t[2]));
    return now.getTime() <= at.getTime() + 3_600_000;
  }
  // Date-only: hide the day after — visible for the whole of its own day.
  return now.getTime() < new Date(y, mo, d + 1).getTime();
}

/** The feed's rows: live opportunities only. The count tag is this list's length. */
export function liveOpportunities(items: readonly OpportunityDto[] | undefined, now?: Date): OpportunityDto[] {
  return liveFeedItems(items, now);
}

/**
 * The live subset of ANY dated feed source, by the one expiry rule. The card has two sources — in-band
 * opportunities and the show/hold invites addressed to the caller (the reference: "a show_hold from Booking
 * renders as an opportunity card") — and both must obey it, or the count tag and the rows disagree.
 */
export function liveFeedItems<T extends FeedTiming>(items: readonly T[] | undefined, now?: Date): T[] {
  return (items ?? []).filter((i) => isLive(i, now));
}

/**
 * A row the viewer has ANSWERED — accepted OR declined — leaves their feed entirely (Ryan, 2026-09-16:
 * *"once you accept an opp it goes to your calendar, doesn't stay in opps"*, extending the 2026-08-29
 * decline ruling). The receiving feed is a to-answer queue: an accepted invite has moved to the schedule /
 * calendar and a declined one is dismissed, so neither belongs here once decided. This is also the spec's
 * own rule — *"receiving items leave when answered"*.
 *
 * Display-only, exactly like the expiry rule above it: nothing is deleted, the organizer still sees the
 * accepted/declined ring on their side, and a RE-INVITE brings the row straight back — an opportunity's
 * re-invite mints a fresh `pending` invite, and a show's re-opens the member row to `pending`.
 *
 * One predicate for both feed families, because they answer the question differently: a show invite carries
 * the caller's own `myRsvp`, while an opportunity carries the whole invite list and the viewer has to be
 * found in it. No viewer id ⇒ nothing is hidden: a missing `personId` must never silently empty the feed.
 * A row where the viewer is not an invitee at all (their own SENDING card) is never answered by them and so
 * always stays.
 */
export function answeredByViewer(item: FeedAnswer, myPersonId: string | null | undefined): boolean {
  if (item.myRsvp != null) return ringOf(item.myRsvp) !== "pending";
  if (!myPersonId) return false;
  return (item.invites ?? []).some(
    (i) => i.inviteePersonId === myPersonId && ringOf(i.response) !== "pending",
  );
}

/**
 * Whether an OPPORTUNITY has left the viewer's Opportunities card for their calendar (ART-338, PO):
 * - an invitee who has answered — in or out — is done with it: "in" puts it on their calendar, "out" dismisses it;
 * - its creator keeps it until EVERYONE they invited is in (they get the "Everyone you invited is in" bell and it
 *   lands on their calendar). A decline keeps it on the card, which says who passed. One with nobody invited
 *   stays, since there is nobody to wait on.
 * Display-only, like the expiry rule: a re-invite mints a fresh pending invite and brings the row straight back.
 * No viewer id ⇒ nothing is hidden.
 */
export function oppLeftFeed(
  opp: { createdByPersonId?: string | null } & FeedAnswer,
  myPersonId: string | null | undefined,
): boolean {
  if (!myPersonId) return false;
  const invites = opp.invites ?? [];
  if (opp.createdByPersonId === myPersonId)
    return invites.length > 0 && invites.every((i) => ringOf(i.response) === "accepted");
  return answeredByViewer(opp, myPersonId);
}

/**
 * A response → the reference's ring class (accepted / declined / pending). Opportunity invites and show
 * members share the SAME 'in' / 'out' / 'pending' vocabulary, so one mapper serves both — and it lives here
 * rather than in either card so the desktop feed and the mobile home read the same three states.
 */
export function ringOf(response: string | null | undefined): "accepted" | "declined" | "pending" {
  return response === "in" ? "accepted" : response === "out" ? "declined" : "pending";
}

/**
 * Pending is the ONLY state that can still be answered — an RSVP already given is done, and its buttons go
 * away rather than inviting the same person to answer twice. An ANSWERED row (accepted or declined) does not
 * reach this at all — it has already left the feed (see `answeredByViewer`) — so in practice this only ever
 * gates a pending row. An unknown/absent value reads as pending, the safe direction — the alternative
 * silently withholds the control from someone who has not answered.
 */
export function canRsvp(response: string | null | undefined): boolean {
  return ringOf(response) === "pending";
}

/**
 * The feed's sort key — date then start time, soonest first. Undated items sort LAST: they never expire, so
 * sorting them by an absent date would pin them to the top of the feed forever.
 */
export function feedSortKey(item: FeedTiming): string {
  return item.date ? `${item.date}T${item.startTime ?? "00:00"}` : "9999";
}

/** "5:00 PM" from a stored 24h wall clock. Display is ALWAYS 12-hour; the stored value stays 24h. */
export function oppTime(value: string | null | undefined): string | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(value ?? "");
  if (!m) return null;
  const h = Number(m[1]);
  return `${((h + 11) % 12) + 1}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
}

/** "Aug 16" / "Aug 16 · 5:00 PM" — the row's when-line, with no timezone shift. */
export function oppWhen(opp: FeedTiming): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(opp.date ?? "");
  const day = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, {
        month: "short", day: "numeric",
      })
    : null;
  return [day, oppTime(opp.startTime)].filter(Boolean).join(" · ") || null;
}

/**
 * PR subtype / format keys → display labels. THE ONE SOURCE both the create pickers (`NewDealModal` builds
 * its option lists from these) and the card meta read, so a stored key can never render raw ("in_person") on
 * one surface while the picker says "In person" on another — the drift that had the mobile Type cell printing
 * `podcast · in_person`. Insertion order IS the picker order. An unknown key title-cases (never raw snake_case).
 */
export const PR_SUBTYPE_LABELS: Record<string, string> = {
  podcast: "Podcast", radio: "Radio", meeting: "Meeting", placement: "Placement", other: "Other",
};
export const PR_FORMAT_LABELS: Record<string, string> = {
  online: "Online", in_person: "In person",
};

/** Title-case an unknown enum key so a value we forgot to map still reads as words, never `some_key`. */
function humanizeKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A PR subtype key → its label (null passes through); an unmapped key title-cases rather than rendering raw. */
export function prSubtypeLabel(key: string | null | undefined): string | null {
  return key ? (PR_SUBTYPE_LABELS[key] ?? humanizeKey(key)) : null;
}

/** A PR format key → its label (`in_person` → "In person"); an unmapped key title-cases. */
export function prFormatLabel(key: string | null | undefined): string | null {
  return key ? (PR_FORMAT_LABELS[key] ?? humanizeKey(key)) : null;
}

/**
 * Our five stored types → the reference's badge label. PR carries its subtype ("PR · Podcast"), resolved to a
 * LABEL, never the raw key. Lives here rather than in the card so the de-dupe below and both surfaces read the
 * same label.
 */
export function oppTypeLabel(opp: { type?: string | null; prSubtype?: string | null }): string {
  const base = oppTypeBase(opp.type);
  const sub = opp.type === "pr" && opp.prSubtype ? ` · ${prSubtypeLabel(opp.prSubtype)}` : "";
  return `${base}${sub}`;
}

/** The bare type word, without a PR subtype — what a stored name may redundantly repeat. */
function oppTypeBase(type: string | null | undefined): string {
  return (
    type === "pr" ? "PR"
    : type === "write" ? "Write"
    : type === "rehearsal" ? "Rehearsal"
    : type === "content" ? "Content"
    : type === "other" ? "Other"
    : (type ?? "Opportunity")
  );
}

/**
 * The row's name and its place, de-duplicated — the reference's "Opportunity title · City, ST".
 *
 * `title` is what the opportunity IS ("Full-band rehearsal"); `locationName` is WHERE ("City, ST"). The
 * legacy card's `oppTitleParts` exists because stored names often already embed the type and/or the place
 * ("Rehearsal · The Echo"), and printing that beside a separate location line rendered the same words twice.
 *
 * Three rules, in order:
 * 1. **Strip a leading "&lt;Type&gt; · "** — the pill above already says the type, so echoing it reads like a bug.
 * 2. **Never a nameless row.** With no title the location names it; an ONLINE opportunity names itself
 *    "Online" (the change request: a PR opportunity always carries a location, a city/state or the literal
 *    "Online" — never a location-less card); failing all that, "Untitled", exactly as the legacy card did.
 *    A row that is only a pill, a date and some faces tells you nothing about what it IS.
 * 3. **Show the place only when it ADDS information** — not when either string contains the other.
 */
export function oppTitleParts(opp: {
  type?: string | null;
  prSubtype?: string | null;
  prFormat?: string | null;
  title?: string | null;
  locationName?: string | null;
}): { title: string; loc: string } {
  const place = (opp.locationName ?? "").trim();
  const online = opp.prFormat === "online";

  let title = (opp.title ?? "").trim();
  const prefix = `${oppTypeBase(opp.type)} · `;
  if (title.toLowerCase().startsWith(prefix.toLowerCase())) title = title.slice(prefix.length).trim();
  if (!title) title = place || (online ? "Online" : "") || "Untitled";

  // An online opportunity's WHERE is the literal word — it has nowhere to be. Suppressed when the name
  // already fell back to it, so a row can never read "Online · Online".
  if (online) return { title, loc: title === "Online" ? "" : "Online" };

  const loc = place && !title.includes(place) && !place.includes(title) ? place : "";
  return { title, loc };
}

/**
 * "$500" from the stored int64 cents — the AMOUNT half of a row's money line.
 *
 * Whole dollars, thousands-separated, matching the reference's `$0,000` and the legacy card's
 * `'$' + Number(money).toLocaleString()`. Blank (null) for a missing value, so a caller can join it away:
 * the legacy modal's field was OPTIONAL and most opportunities carry nothing.
 *
 * Cents are never rendered. The legacy field was free text ("$500, door split") and could say anything;
 * ours is a typed amount, so a half-dollar would be a rounding artifact of the input, not a real figure.
 */
export function oppMoney(cents: number | null | undefined): string | null {
  return formatMoneyCents(cents) || null;
}

/**
 * The money TERMS — the half of the legacy "Money (optional)" field that was never a number ("door split",
 * "plus 10% of merch"). Trimmed; blank in → null, so a caller can join it away.
 *
 * Deliberately NOT folded into {@link oppMoney}: the amount is mono chrome, and STYLE-GUIDE standing
 * ruling 8 keeps mono to figures, dates and codes — "no text over 24 chars renders mono". A free-text note
 * belongs in body voice, which is why each surface places this one itself rather than getting it joined in.
 */
export function oppMoneyNote(notes: string | null | undefined): string | null {
  return notes?.trim() || null;
}
